import React, { useEffect, useState } from 'react';
import { useAsync } from 'react-use';
import { useNavigate } from 'react-router-dom';

import { UserEntity, GroupEntity } from '@backstage/catalog-model';
import {
  InfoCard,
  InfoCardVariants,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { alertApiRef, useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  entityRouteRef,
  useEntity,
} from '@backstage/plugin-catalog-react';

import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import HelpIcon from '@material-ui/icons/Help';
import HomeIcon from '@material-ui/icons/Home';
import PersonIcon from '@material-ui/icons/Person';

import { ApptioGroupInfoEntity } from 'plugin-apptio-catalog-common';
import { EntityGroupInfoPromptCard } from './EntityGroupInfoPromptCard';
import { SlackIcon } from '../../custom-icons';

const USER_PREFIX = 'user:';

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    clickable: {
      cursor: 'pointer',
    },
  }),
);

const CardTitle = (props: { title: string }) => (
  <Box display="flex" alignItems="center">
    <HelpIcon fontSize="inherit" />
    <Box ml={1}>{props.title}</Box>
  </Box>
);

type Props = {
  variant?: InfoCardVariants;
};

export const EntityGroupSupportCard = (props: Props) => {
  const { variant } = props;
  const classes = useStyles();
  const { entity: group } = useEntity<GroupEntity>();

  // Null asserts no GroupInfo exists for this Group
  const [groupInfo, setGroupInfo] = useState<ApptioGroupInfoEntity | null>();

  const alertApi = useApi(alertApiRef);
  const catalogApi = useApi(catalogApiRef);

  const navigate = useNavigate();
  const entityRoute = useRouteRef(entityRouteRef);

  const openSlackChannel = (channel: string | undefined) => {
    if (!channel) return;
    window.open(`https://slack.com/app_redirect?channel=${channel}`, '_blank');
  };

  useEffect(() => {
    (async () => {
      const entity = (await catalogApi.getEntityByRef({
        kind: 'GroupInfo',
        name: group.metadata.name, // 1:1
        namespace: group.metadata.namespace || 'default',
      })) as ApptioGroupInfoEntity | undefined;
      setGroupInfo(entity ?? null);
    })();
  }, []);

  const getOwnersForGroup = async () => {
    const entities = [];
    for (let owner of groupInfo?.spec.owners ?? []) {
      if (owner.startsWith(USER_PREFIX)) {
        owner = owner.slice(USER_PREFIX.length);
      }
      const entity = (await catalogApi.getEntityByRef({
        kind: 'User',
        name: owner,
        namespace: 'default',
      })) as UserEntity;
      if (entity) {
        entities.push(entity);
      } else {
        alertApi.post({
          message: `User ${owner} does not exist`,
          severity: 'warning',
        });
      }
    }
    return entities;
  };

  let {
    value: ownerEntities,
    loading,
    error,
  } = useAsync(getOwnersForGroup, [groupInfo]);

  if (groupInfo === undefined || loading) {
    return <Progress />;
  } else if (error) {
    return <ResponseErrorPanel error={error} />;
  } else if (groupInfo === null) {
    return <EntityGroupInfoPromptCard variant={variant} />;
  } else if (!ownerEntities) {
    ownerEntities = [];
  }

  const info = groupInfo.spec;

  const slackContent = [
    ['Team', info.slackChannels.team],
    ['Support', info.slackChannels.support],
  ]
    .filter(([_, name]) => !!name)
    .map(([label, name]) => {
      if (name!.startsWith('#')) name = name!.slice(1);
      return (
        <ListItem
          className={classes.clickable}
          onClick={() => openSlackChannel(name)}
        >
          <ListItemIcon>
            <SlackIcon />
          </ListItemIcon>
          <ListItemText primary={`#${name}`} secondary={`${label} Channel`} />
        </ListItem>
      );
    });

  const ownersContent = [];
  for (let entity of ownerEntities) {
    const entityLink = entityRoute({
      kind: entity.kind,
      name: entity.metadata.name,
      namespace: entity.metadata.namespace || 'default',
    });

    const ownerName = entity.spec.profile?.displayName ?? entity.metadata.name;

    ownersContent.push(
      <ListItem
        className={classes.clickable}
        onClick={() => navigate(entityLink)}
      >
        <ListItemIcon>
          <PersonIcon />
        </ListItemIcon>
        <ListItemText primary={ownerName} secondary="Owner" />
      </ListItem>,
    );
  }

  const openHomepage = (homepage: string) => {
    let target = '_blank';
    try {
      if (new URL(homepage).hostname === window.location.hostname) {
        // If navigating elsewhere in Backstage, open in same tab
        target = '_self';
      }
    } catch {
      /** YOLO the user-provided URL */
    }
    window.open(homepage ?? undefined, target);
  };

  return (
    <InfoCard title={<CardTitle title="Support" />} variant={variant}>
      <List>
        <ListItem
          className={classes.clickable}
          onClick={() => openHomepage(info.homepage.url)}
        >
          <ListItemIcon>
            <HomeIcon />
          </ListItemIcon>
          <ListItemText primary={info.homepage.name} secondary="Homepage" />
        </ListItem>
        {slackContent}
        {ownersContent}
      </List>
    </InfoCard>
  );
};
